/**
 * Suppress the "Invalid vr type <X> - using <Y>" chatter that dcmjs emits
 * for every DICOM element with a non-standard Value Representation code.
 *
 * DICOM has a fixed set of VR codes (CS, DS, OB, OW, US, etc.). Many
 * source files carry vendor-private or anonymizer-corrupted VR fields
 * (`ox`, `xs`, etc.) and dcmjs (bundled into CS3D 5's dicom-image-loader)
 * warns on every one, without a way to disable via config. On a real CT
 * study this floods the console with hundreds of identical lines and
 * makes actual errors impossible to spot.
 *
 * We intercept `console.warn` / `console.log` and drop only the messages
 * matching this exact pattern. Everything else passes through untouched.
 * Call once at app startup.
 */
export function silenceDicomVrNoise(): void {
  if ((window as any).__edgewebVrNoiseSilenced) return;
  (window as any).__edgewebVrNoiseSilenced = true;

  const VR_NOISE_RE = /^Invalid vr type .+ - using \w+$/;

  function wrap(method: 'log' | 'warn' | 'info' | 'error') {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      // Fast-path: only inspect string first-arg messages
      if (typeof args[0] === 'string' && VR_NOISE_RE.test(args[0])) return;
      original(...args);
    };
  }

  wrap('warn');
  wrap('log');
  // Not intercepting error — dcmjs uses warn/log for this, and any real
  // error is worth surfacing even if it happens to match the pattern.
}

// Run at module-graph evaluation time so we wrap console BEFORE any
// other module (App → CS3D → dcmjs) starts logging. ES module side-
// effects run in import order; a side-effect import from main.tsx
// gives us the earliest possible hook point.
silenceDicomVrNoise();
